import { useRef, useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  UploadCloud,
  Loader2,
  ImageOff,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { uploadGlimpseImage, resolveMedia } from "@/api/client";

export const CARD_TYPES = [
  "FEEL",
  "STORY",
  "SURPRISE",
  "LENS",
  "REFLECT",
  "FEEDBACK",
  "CURIOSITY",
  "BHĀGAVATAM",
];

const inputCls =
  "w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50";

const OPTION_KEYS = ["A", "B", "C", "D", "E", "F"];

// Single card editor — supports every card type + an optional hero image.
export const CardEditor = ({ card, index, total, onChange, onRemove, onMove }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => onChange({ ...card, ...patch });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { path } = await uploadGlimpseImage(file);
      set({ image: path, show_image: true });
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed (max 10MB · jpg/png/webp/gif)");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setOption = (i, text) =>
    set({
      options: (card.options || []).map((o, oi) =>
        oi === i ? { ...o, text } : o
      ),
    });
  const setOptionHi = (i, text_hi) =>
    set({
      options: (card.options || []).map((o, oi) =>
        oi === i ? { ...o, text_hi } : o
      ),
    });
  const addOption = () => {
    const opts = card.options || [];
    if (opts.length >= OPTION_KEYS.length) return;
    set({ options: [...opts, { key: OPTION_KEYS[opts.length], text: "" }] });
  };
  const removeOption = (i) =>
    set({
      options: (card.options || [])
        .filter((_, oi) => oi !== i)
        .map((o, oi) => ({ ...o, key: OPTION_KEYS[oi] })),
    });

  return (
    <div
      data-testid={`card-editor-${index}`}
      className="rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full border border-amber-300/30 text-xs font-semibold text-amber-200">
          {index + 1}
        </span>
        <select
          data-testid={`card-type-${index}`}
          value={card.type}
          onChange={(e) => set({ type: e.target.value })}
          className="rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-amber-200 outline-none focus:border-amber-300/50"
        >
          {CARD_TYPES.map((t) => (
            <option key={t} value={t} className="bg-[#0d1226]">
              {t}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            className="rounded-md border border-amber-300/20 p-1.5 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onMove(index, 1)}
            disabled={index === total - 1}
            className="rounded-md border border-amber-300/20 p-1.5 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`card-remove-${index}`}
            onClick={() => onRemove(index)}
            className="rounded-md border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <label className="mb-1.5 block text-xs text-amber-100/50">Title / headline</label>
      <input
        data-testid={`card-title-${index}`}
        value={card.title || ""}
        onChange={(e) => set({ title: e.target.value })}
        className={`${inputCls} mb-2`}
      />
      <input
        data-testid={`card-title-hi-${index}`}
        value={card.title_hi || ""}
        onChange={(e) => set({ title_hi: e.target.value })}
        placeholder="हिंदी शीर्षक (Hindi title)"
        className={`${inputCls} mb-3`}
      />

      {card.type !== "REFLECT" && (
        <>
          <label className="mb-1.5 block text-xs text-amber-100/50">
            {card.type === "FEEDBACK" ? "Explanation" : "Body"}
          </label>
          <textarea
            data-testid={`card-body-${index}`}
            value={card.body || ""}
            onChange={(e) => set({ body: e.target.value })}
            rows={3}
            className={`${inputCls} mb-2 resize-y`}
          />
          <textarea
            data-testid={`card-body-hi-${index}`}
            value={card.body_hi || ""}
            onChange={(e) => set({ body_hi: e.target.value })}
            rows={2}
            placeholder="हिंदी (Hindi body)"
            className={`${inputCls} mb-3 resize-y`}
          />
        </>
      )}

      {card.type === "REFLECT" && (
        <div className="mb-3">
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-xs text-amber-100/50">Answer options</label>
            <button
              onClick={addOption}
              className="inline-flex items-center gap-1 rounded-full border border-amber-300/25 px-2.5 py-1 text-[11px] text-amber-200 hover:bg-amber-400/10"
            >
              <Plus className="h-3 w-3" /> Option
            </button>
          </div>
          <div className="space-y-2">
            {(card.options || []).map((o, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-2 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-amber-400/15 text-xs font-bold text-amber-300">
                  {o.key}
                </span>
                <div className="flex-1 space-y-1.5">
                  <input
                    value={o.text}
                    onChange={(e) => setOption(i, e.target.value)}
                    className={inputCls}
                  />
                  <input
                    value={o.text_hi || ""}
                    onChange={(e) => setOptionHi(i, e.target.value)}
                    placeholder="हिंदी (Hindi option)"
                    className={inputCls}
                  />
                </div>
                <button
                  onClick={() => removeOption(i)}
                  className="mt-1 rounded-md border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {card.type === "FEEDBACK" && (
        <div className="mb-3">
          <label className="mb-1.5 block text-xs text-amber-100/50">Correct option</label>
          <select
            value={card.correct_key || ""}
            onChange={(e) => set({ correct_key: e.target.value })}
            className="rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 outline-none focus:border-amber-300/50"
          >
            <option value="" className="bg-[#0d1226]">—</option>
            {OPTION_KEYS.map((k) => (
              <option key={k} value={k} className="bg-[#0d1226]">
                {k}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ---- Image section (optional per card) ---- */}
      <div className="rounded-xl border border-amber-300/10 bg-[#080b16]/40 p-3">
        <div className="flex items-center gap-3">
          <div className="grid h-16 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border border-amber-300/10 bg-[#0d1226]">
            {card.image ? (
              <img
                src={resolveMedia(card.image)}
                alt={card.alt || ""}
                className="h-full w-full object-cover"
              />
            ) : (
              <ImageOff className="h-5 w-5 text-amber-100/25" />
            )}
          </div>
          <div className="flex-1">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid={`card-image-input-${index}`}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UploadCloud className="h-3.5 w-3.5" />
                )}
                {card.image ? "Replace" : "Upload image"}
              </button>
              {card.image && (
                <button
                  onClick={() => set({ image: "", show_image: false })}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/20 px-3 py-1.5 text-xs text-red-300/80 hover:bg-red-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
              <label className="ml-1 flex items-center gap-1.5 text-xs text-amber-100/70">
                <input
                  type="checkbox"
                  checked={!!card.show_image}
                  onChange={(e) => set({ show_image: e.target.checked })}
                  className="h-3.5 w-3.5 accent-amber-400"
                />
                Show image
              </label>
            </div>
            <input
              value={card.alt || ""}
              onChange={(e) => set({ alt: e.target.value })}
              placeholder="Alt text (accessibility)"
              className={`${inputCls} mt-2`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
